import {afterNextRender, Component, computed, input, output, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FormControl, FormGroup} from '@angular/forms';
import {InvoiceLineModelForm} from '../invoice-form/invoice-form';
import {TranslatePipe} from '@ngx-translate/core';
import {TextInput} from '../../../../components/text-input/text-input';
import {TextArea} from '../../../../components/text-area/text-area';
import {NumericInput} from '../../../../components/numeric-input/numeric-input';
import {TaxDropdown} from '../tax-dropdown/tax-dropdown';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {startWith, switchMap} from 'rxjs';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {InvoiceProductDropdown} from '../invoice-product-dropdown/invoice-product-dropdown';
import {InvoiceTimesheetPicker} from '../invoice-timesheet-picker/invoice-timesheet-picker';
import {ProductDetails, TimesheetSummary} from '../../../../../api';

@Component({
  selector: 'elcia-invoice-line-form',
  imports: [
    TranslatePipe,
    DatePipe,
    TextInput,
    TextArea,
    NumericInput,
    TaxDropdown,
    ElciaButton,
    InvoiceProductDropdown,
    InvoiceTimesheetPicker,
  ],
  templateUrl: './invoice-line-form.html',
  styleUrl: './invoice-line-form.scss',
})
export class InvoiceLineForm {
  fg = input.required<FormGroup<InvoiceLineModelForm>>();
  legalEntityId = input.required<number | null>();
  customerId = input.required<number | null>();
  invoiceDate = input.required<Date | null>();
  amountOfLine = new FormControl({value: 0, disabled: true});
  onRemove = output();
  // Total des frais de déplacement des prestations sélectionnées (0 = pas de ligne dédiée).
  travelExpensesResolved = output<number>();

  protected readonly timesheetPickerOpen = signal(false);
  protected readonly currentTimesheetTypeId = signal<number | null>(null);
  protected readonly descriptionVisible = signal(false);
  protected readonly canOpenTimesheetPicker = computed(() =>
    this.currentTimesheetTypeId() != null && this.customerId() != null,
  );

  // Ligne « Frais de déplacement » d'une prestation : pas de sélection de timesheets possible
  protected readonly isTravelExpenses = computed(() => this.fg().getRawValue().isTravelExpenses);

  // Ligne générée par la facturation automatique : produit verrouillé, période affichée si le contrat a des dates
  protected readonly isGeneratedLine = computed(() => this.fg().getRawValue().customerProductBillingId != null);
  protected readonly billingPeriodStart = computed(() => this.fg().getRawValue().billingPeriodStart);
  protected readonly billingPeriodEnd = computed(() => this.fg().getRawValue().billingPeriodEnd);

  constructor() {
    toObservable(this.fg).pipe(
      switchMap(fg => fg.valueChanges.pipe(startWith(fg.getRawValue()))),
      takeUntilDestroyed(),
    ).subscribe(value => {
      const quantity = value.quantity ?? 0;
      const unitPrice = value.unitPrice ?? 0;
      this.amountOfLine.setValue(quantity * unitPrice);
    });

    afterNextRender(() => {
      if (this.fg().controls.description.value) this.descriptionVisible.set(true);
    });
  }

  protected toggleDescription(): void {
    this.descriptionVisible.update(visible => !visible);
  }

  protected onProductSelected(product: ProductDetails | null): void {
    const typeId = product?.timesheetType?.id ?? null;
    this.currentTimesheetTypeId.set(typeId);
    if (typeId === null) {
      this.fg().controls.timesheetIds.setValue([]);
      this.travelExpensesResolved.emit(0);
    }
  }

  protected onTimesheetsConfirmed(timesheets: TimesheetSummary[]): void {
    this.fg().controls.timesheetIds.setValue(timesheets.map(t => t.id));

    const totalSeconds = timesheets.reduce((acc, t) => acc + t.billableDurationInSeconds, 0);
    const totalHours = Math.round((totalSeconds / 3600) * 100) / 100;
    this.fg().controls.quantity.setValue(totalHours);

    const travelExpenses = timesheets.reduce((acc, t) => acc + (t.travelExpensesAmount ?? 0), 0);
    this.travelExpensesResolved.emit(travelExpenses);

    this.timesheetPickerOpen.set(false);
  }

  protected remove(): void {
    this.onRemove.emit();
  }
}
